import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { IExternalFormMultiselectItem } from 'components/ExternalFormMultiselect';
import type { PriceListUerRowBase } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import {
    priceListUerCodeFilter,
    priceListUerNameFilter,
    uerPriceListUerGroupDataFilterGql,
} from 'pages/Project/UerProject/PriceList/PriceListUER/uerQueries';
import { Checkbox } from 'uikit';

import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

export interface ICheckedChangeItem {
    item: IExternalFormMultiselectItem;
    isChecked: boolean;
}

interface IUsePriceListUerForTechPartRatioColumnsProps {
    onRowCheckedChange: (changeState: ICheckedChangeItem[]) => void;
    checkedIds: number[];
    disabledIds: number[];
    onFiltersUpdated: ({ fieldName, checkedValues }: { fieldName: string; checkedValues: string[] }) => void;
}

export const getPriceListUerBaseColumns = (
    filterVariables: {
        contractId: number;
    },
    onFiltersUpdated: ({ fieldName, checkedValues }: { fieldName: string; checkedValues: string[] }) => void,
): ColDef<PriceListUerRowBase>[] => [
    {
        headerName: 'Подраздел',
        field: 'groupName',
        flex: 2,
        filter: AgGridCustomFilterAdapter,
        filterParams: {
            hideTableHeader: true,
            variables: filterVariables,
            changeFilterHandler: onFiltersUpdated,
            dataAccessor: 'uerPriceListUerGroupDataFilter',
            whereVariables: ['value'],
            childFieldAccessor: 'value',
            columns: [
                {
                    headerName: 'Подраздел',
                    field: 'value',
                    checkboxSelection: true,
                    width: 400,
                },
            ],
            query: uerPriceListUerGroupDataFilterGql,
        },
    },
    {
        headerName: 'Шифр',
        field: 'code',
        flex: 1,
        filter: AgGridCustomFilterAdapter,
        filterParams: {
            hideTableHeader: true,
            variables: filterVariables,
            changeFilterHandler: onFiltersUpdated,
            dataAccessor: 'uerPriceListUerCodeFilter',
            whereVariables: ['value'],
            childFieldAccessor: 'value',
            columns: [
                {
                    headerName: 'Шифр',
                    field: 'value',
                    checkboxSelection: true,
                    width: 400,
                },
            ],
            query: priceListUerCodeFilter,
        },
    },
    {
        headerName: 'Наименование',
        field: 'name',
        flex: 2,
        filter: AgGridCustomFilterAdapter,
        filterParams: {
            hideTableHeader: true,
            variables: filterVariables,
            changeFilterHandler: onFiltersUpdated,
            dataAccessor: 'uerPriceListUerNameFilter',
            whereVariables: ['value'],
            childFieldAccessor: 'value',
            columns: [
                {
                    headerName: 'Подраздел',
                    field: 'value',
                    checkboxSelection: true,
                    width: 400,
                },
            ],
            query: priceListUerNameFilter,
        },
    },
    {
        headerName: 'Ед. изм.',
        field: 'unit',
        width: 100,
    },
];

export const usePriceListUerForTechPartRatioColumns = (props: IUsePriceListUerForTechPartRatioColumnsProps) => {
    const { onRowCheckedChange, checkedIds, disabledIds, onFiltersUpdated } = props;

    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId);

    const filterVariables = useMemo(
        () => ({
            contractId: projectData?.contractId,
        }),
        [projectData?.contractId],
    );

    return useMemo<ColDef<PriceListUerRowBase>[]>(
        () => [
            {
                width: 55,
                resizable: false,
                cellRenderer: ({ data }: { data: PriceListUerRowBase }) => {
                    const { id, code } = data ?? {};
                    const isDisabled = disabledIds.includes(id);

                    return (
                        <Checkbox
                            disabled={isDisabled}
                            checked={checkedIds.includes(id)}
                            onChange={({ checked }) => {
                                const items = [{ item: { id, label: code, data }, isChecked: checked }];

                                onRowCheckedChange(items);
                            }}
                        />
                    );
                },
                lockPinned: true,
                pinned: 'left',
            },
            ...getPriceListUerBaseColumns(filterVariables, onFiltersUpdated),
        ],
        [checkedIds, disabledIds, filterVariables, onFiltersUpdated, onRowCheckedChange],
    );
};
